import { motion } from "framer-motion";
import { useProducts, money, cleanTitle } from "@/lib/api";
import { useCart, toLine } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { BUNDLE_HANDLES } from "@/lib/content";
import { Reveal, SplitText, Magnetic } from "@/components/fx/Motion";

export const Bundle = () => {
  const { data = [] } = useProducts({ collection: "bellecare" });
  const { add, checkout } = useCart();
  const { t } = useI18n();
  const items = BUNDLE_HANDLES.map((h) => data.find((p) => p.handle === h)).filter(Boolean);
  if (items.length < 2) return null;
  const lines = items.map((p) => toLine(p, p.variants[0]));
  const total = items.reduce((s, p) => s + p.price, 0);
  const compare = items.reduce((s, p) => s + (p.compare_at_price || p.price), 0);
  const addAll = () => lines.forEach((l, i) => add(l, i === lines.length - 1, i === lines.length - 1));

  return (
    <section data-testid="bundle-section" className="max-w-7xl mx-auto px-5 lg:px-10 py-28 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
      <div className="grid grid-cols-3 gap-3">
        {items.map((p, i) => (
          <motion.div key={p.id} className="bg-white aspect-[3/4] overflow-hidden" initial={{ opacity: 0, y: 120, rotate: (i - 1) * 6 }}
            whileInView={{ opacity: 1, y: i === 1 ? 32 : 0, rotate: 0 }} viewport={{ once: true }} whileHover={{ y: i === 1 ? 16 : -16, scale: 1.03 }}
            transition={{ duration: 1.1, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}>
            <img src={p.images[0]} alt={p.title} className="h-full w-full object-contain p-3" />
          </motion.div>
        ))}
      </div>
      <div>
        <p className="eyebrow mb-4">{t("bundle_eyebrow")}</p>
        <h2 className="font-display text-3xl lg:text-5xl leading-tight mb-5"><SplitText text={t("bundle_title_1")} /> <em className="text-gold"><SplitText text={t("bundle_title_2")} delay={0.25} /></em></h2>
        <Reveal><p className="text-white/60 mb-6 max-w-md">{t("bundle_text")}</p></Reveal>
        <ul className="space-y-2 mb-8">
          {items.map((p, i) => (
            <Reveal as="li" key={p.id} delay={0.1 * i} y={12} className="flex justify-between text-sm border-b border-white/5 pb-2">
              <span className="text-white/80">{cleanTitle(p.title)}</span><span className="text-white/50">{money(p.price)}</span>
            </Reveal>
          ))}
        </ul>
        <div className="flex flex-wrap items-baseline gap-3 mb-6">
          <span data-testid="bundle-total" className="text-3xl font-semibold">{money(total)}</span>
          {compare > total && <span className="line-through text-white/40">{money(compare)}</span>}
          {compare > total && <span className="text-gold text-sm font-semibold">{t("you_save", { amount: money(compare - total) })}</span>}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Magnetic><button data-testid="bundle-buy-now-button" onClick={() => checkout(lines)} className="btn-gold btn-shine h-14 px-10 w-full">{t("bundle_buy")}</button></Magnetic>
          <Magnetic><button data-testid="bundle-add-button" onClick={addAll} className="btn-ghost h-14 px-10 w-full">{t("bundle_add")}</button></Magnetic>
        </div>
      </div>
    </section>
  );
};
